import { useState, useCallback } from 'react';
import { buscarPersonaPorCuil } from '../../../services/personas.service';

/**
 * Custom Hook para gestionar el formulario de NotaDeAutorizacionNuevo
 * con sincronización centralizada de datos de personas por CUIL en tiempo real.
 */
export const usePersonSync = (initialState = {}) => {
  const [formData, setFormData] = useState({
    area: '',
    areaCorreo: '',
    areaCelular: '',
    cursos: [],
    coordinador: { cuil: '', nombre: '', apellido: '', mail: '', celular: '' },
    autorizador: { cuil: '', nombre: '', apellido: '', descripcionCargo: '', mail: '', celular: '' },
    publicoObjetivo: '',
    ventajas: '',
    tutores: [{ cuil: '', nombre: '', apellido: '', mail: '', celular: '', rol: '' }],
    sga: [{ cuil: '', nombre: '', apellido: '', mail: '', celular: '' }],
    comunicacion: '',
    ...initialState
  });

  // Registro centralizado de personas por CUIL: { [cuil]: { cuil, nombre, apellido, mail, celular } }
  const [personsRegistry, setPersonsRegistry] = useState({});

  /**
   * Sincroniza un campo de contacto (mail o celular) para un CUIL específico
   * en TODAS las secciones del formulario (autorizador, coordinador, tutores, sga)
   * y en el registro centralizado.
   */
  const updateContactForCuil = useCallback((cuil, field, value) => {
    if (!cuil) return;

    const normalizedCuil = String(cuil).trim();

    setPersonsRegistry((prevRegistry) => ({
      ...prevRegistry,
      [normalizedCuil]: {
        ...prevRegistry[normalizedCuil],
        cuil: normalizedCuil,
        [field]: value
      }
    }));

    setFormData((prev) => {
      const nextFormData = { ...prev };

      // 1. Autorizador
      if (nextFormData.autorizador && String(nextFormData.autorizador.cuil).trim() === normalizedCuil) {
        nextFormData.autorizador = {
          ...nextFormData.autorizador,
          [field]: value
        };
      }

      // 2. Coordinador
      if (nextFormData.coordinador && String(nextFormData.coordinador.cuil).trim() === normalizedCuil) {
        nextFormData.coordinador = {
          ...nextFormData.coordinador,
          [field]: value
        };
      }

      // 3. Tutores
      if (Array.isArray(nextFormData.tutores)) {
        nextFormData.tutores = nextFormData.tutores.map((tutor) => {
          if (String(tutor.cuil).trim() === normalizedCuil) {
            return { ...tutor, [field]: value };
          }
          return tutor;
        });
      }

      // 4. Accesos SGA
      if (Array.isArray(nextFormData.sga)) {
        nextFormData.sga = nextFormData.sga.map((sgaItem) => {
          if (String(sgaItem.cuil).trim() === normalizedCuil) {
            return { ...sgaItem, [field]: value };
          }
          return sgaItem;
        });
      }

      return nextFormData;
    });
  }, []);

  /**
   * Busca persona por CUIL. Primero consulta el registro local (si ya fue editado/cargado).
   * Si no existe, realiza la llamada al backend y lo registra.
   */
  const searchPerson = useCallback(async (rawCuil) => {
    if (!rawCuil) return { success: false, message: 'Ingrese un CUIL válido' };
    const cuil = String(rawCuil).trim();

    if (cuil.length !== 11) {
      return { success: false, message: 'Son necesarios 11 caracteres numéricos' };
    }

    // 1. Verificar si ya existe en el registro local
    if (personsRegistry[cuil] && personsRegistry[cuil].nombre) {
      return {
        success: true,
        data: personsRegistry[cuil],
        isFromRegistry: true
      };
    }

    // 2. Si no existe localmente, consultar backend API
    try {
      const response = await buscarPersonaPorCuil(cuil);
      if (response.success && response.data) {
        const personData = {
          cuil,
          nombre: response.data.nombre || '',
          apellido: response.data.apellido || '',
          mail: response.data.mail || response.data.correo || '',
          celular: response.data.celular || ''
        };

        // Guardar en el registro centralizado
        setPersonsRegistry((prev) => ({
          ...prev,
          [cuil]: personData
        }));

        return { success: true, data: personData, isFromRegistry: false };
      }
      return { success: false, message: response.message || 'Persona no encontrada.' };
    } catch (err) {
      return { success: false, message: 'Error al conectar con el servidor.' };
    }
  }, [personsRegistry]);

  /**
   * Registra manualmente una persona en el registro si no existía aún
   */
  const registerPersonData = useCallback((person) => {
    if (!person || !person.cuil) return;
    const cuil = String(person.cuil).trim();
    if (!cuil) return;

    setPersonsRegistry((prev) => ({
      ...prev,
      [cuil]: {
        ...prev[cuil],
        cuil,
        nombre: person.nombre || prev[cuil]?.nombre || '',
        apellido: person.apellido || prev[cuil]?.apellido || '',
        mail: person.mail || prev[cuil]?.mail || '',
        celular: person.celular || prev[cuil]?.celular || ''
      }
    }));
  }, []);

  /**
   * Retorna una lista con las OTRAS secciones donde participa el CUIL dado,
   * para mostrar en la interfaz un distintivo visual (MUI Chip).
   */
  const getOtherRoles = useCallback((rawCuil, currentSection, currentIndex = null) => {
    if (!rawCuil) return [];
    const cuil = String(rawCuil).trim();
    if (!cuil) return [];

    const roles = [];

    // Autorizador
    if (
      currentSection !== 'autorizador' &&
      formData.autorizador &&
      String(formData.autorizador.cuil).trim() === cuil
    ) {
      roles.push('Autorizador/a');
    }

    // Coordinador
    if (
      currentSection !== 'coordinador' &&
      formData.coordinador &&
      String(formData.coordinador.cuil).trim() === cuil
    ) {
      roles.push('Coordinador/a');
    }

    // Tutores
    if (Array.isArray(formData.tutores)) {
      const isPresentInTutores = formData.tutores.some(
        (tutor, idx) => String(tutor.cuil).trim() === cuil && !(currentSection === 'tutores' && currentIndex === idx)
      );
      if (isPresentInTutores) {
        roles.push('Tutor/a');
      }
    }

    // Accesos SGA
    if (Array.isArray(formData.sga)) {
      const isPresentInSga = formData.sga.some(
        (sgaItem, idx) => String(sgaItem.cuil).trim() === cuil && !(currentSection === 'sga' && currentIndex === idx)
      );
      if (isPresentInSga) {
        roles.push('Acceso SGA');
      }
    }

    return roles;
  }, [formData]);

  const resetForm = useCallback(() => {
    setFormData({
      area: '',
      areaCorreo: '',
      areaCelular: '',
      cursos: [],
      coordinador: { cuil: '', nombre: '', apellido: '', mail: '', celular: '' },
      autorizador: { cuil: '', nombre: '', apellido: '', descripcionCargo: '', mail: '', celular: '' },
      publicoObjetivo: '',
      ventajas: '',
      tutores: [{ cuil: '', nombre: '', apellido: '', mail: '', celular: '', rol: '' }],
      sga: [{ cuil: '', nombre: '', apellido: '', mail: '', celular: '' }],
      comunicacion: '',
      ...initialState
    });
    setPersonsRegistry({});
  }, [initialState]);

  return {
    formData,
    setFormData,
    personsRegistry,
    searchPerson,
    updateContactForCuil,
    registerPersonData,
    getOtherRoles,
    resetForm
  };
};

export default usePersonSync;
